import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Auth } from "shared/@auth/Auth";
import { useLocation } from "react-router-dom";
import { createContainer } from "unstated-next";
import { ENVIRONMENT_CONFIG } from "shared/globals";

// Queries (GET)
export function useSubmittedAgendaQueries(
  refetchInterval,
  isAppCalled = false
) {
  const { makeAuthedRequest, hasPermission } = Auth.useContainer();
  const [status, setStatus] = useState("loading");

  const { fetchManagementTeamNotification } = ENVIRONMENT_CONFIG.flags;

  const location = useLocation();
  function pathParam(location) {
    return location === "/management-team/agenda" ? "MT" : "EC"; // possible change back to EC if there is an ec specific API
  }
  const [_location, setLocation] = useState(isAppCalled ? "MT" : null);
  // Derive route param once per location change; avoid setState during render
  useEffect(() => {
    const isAgendaRoute =
      location.pathname === "/management-team/agenda" ||
      location.pathname === "/executive-committee/agenda";
    if (!isAgendaRoute) return;

    const next = pathParam(location.pathname);
    setLocation((prev) => (prev !== next ? next : prev));
  }, [location]);

  // Submitted Agenda Data
  const isEnabled =
    Boolean(_location) &&
    fetchManagementTeamNotification &&
    hasPermission("management-team.*");

  const { data: submittedAgendaData, refetch } = useQuery(
    ["submittedAgenda", _location],
    () =>
      makeAuthedRequest(`/management-team/agenda/${_location}/submitted`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          setStatus("success");
          // Some regions may return empty body; normalize to []
          try {
            const json = await res.json();
            return json ?? [];
          } catch (e) {
            return [];
          }
        } else {
          setStatus("error");
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      enabled: isEnabled,
      refetchInterval: refetchInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { submittedAgenda: data } = useMemo(() => {
    if (submittedAgendaData == null) {
      return {
        submittedAgenda: [],
      };
    } else {
      return {
        submittedAgenda: submittedAgendaData,
      };
    }
  }, [submittedAgendaData]);

  return {
    submittedAgendaData: data,
    submittedAgendaStatus: status,
    refetchAgenda: refetch,
  };
}

// Mutations (POST/PUT/DELETE)
export function useSubmittedAgendaMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  // Submitted Agenda Edit
  const submittedAgendaEditPut = useMutation(
    ["submittedAgendaEdit"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/submitted/edit`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // Delete Submitted Agenda
  const submittedAgendaDelete = useMutation(
    ["submittedAgendaDelete"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/submitted/delete`, {
        method: "DELETE",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // Move Submitted Agenda
  const submittedAgendaMove = useMutation(
    ["submittedAgendaMove"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/submitted/move`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Order Submitted Agenda Items
  const submittedAgendaOrder = useMutation(
    ["submittedAgendaOrder"],
    (agendaObject) =>
      makeAuthedRequest(`/management-team/agenda/submitted/order`, {
        method: "PUT",
        body: JSON.stringify(agendaObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // Submitted Agenda View
  const submittedAgendaViewPut = useMutation(["submittedAgendaView"], () =>
    makeAuthedRequest(`/management-team/agenda/MT/submitted/view`, {
      method: "PUT",
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  return {
    submittedAgendaEditPut,
    submittedAgendaDelete,
    submittedAgendaMove,
    submittedAgendaOrder,
    submittedAgendaViewPut,
  };
}

// Combined for backward compatibility
export function useSubmittedAgenda(refetchInterval, isAppCalled = false) {
  const queries = useSubmittedAgendaQueries(refetchInterval, isAppCalled);
  const mutations = useSubmittedAgendaMutations();

  return {
    ...queries,
    ...mutations,
  };
}

/**
 * Exported as an unstated container for sharing auth state between components.
 * Provider must be mounted higher in the component tree in order to use the container
 * from inside a component.
 */
//  export const SubmittedAgenda = createContainer(useSubmittedAgenda);
